/**
 * @fileoverview Singers Redux slice.
 * @summary Manages singers state (list of singers for an event).
 *
 * @package KaraoNow
 * @module apps/front/src/store/slices/singersSlice
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiRequest } from '../../services/api.js'
import { API_ENDPOINTS } from '../../config/api.js'

/**
 * @summary Singer interface.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export interface Singer {
  id: string
  eventId: string
  pseudo: string
  guestMode: boolean
  songsCount: number
  joinedAt: string | Date
}

/**
 * @summary Singers state interface.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
interface SingersState {
  singers: Singer[]
  isLoading: boolean
  error: string | null
  currentEventId: string | null
}

/**
 * @summary Initial singers state.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
const initialState: SingersState = {
  singers: [],
  isLoading: false,
  error: null,
  currentEventId: null,
}

/**
 * @summary Fetch singers for an event.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const fetchSingers = createAsyncThunk('singers/fetchSingers', async (eventId: string) => {
  const response = await apiRequest<{ singers: Singer[] }>({
    method: 'GET',
    url: API_ENDPOINTS.EVENTS.SINGERS(eventId),
  })

  if (!response.success) {
    throw new Error(response.message || 'Failed to fetch singers')
  }

  return { eventId, singers: response.data!.singers }
})

/**
 * @summary Remove a singer from an event.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const removeSinger = createAsyncThunk(
  'singers/removeSinger',
  async ({ eventId, singerId }: { eventId: string; singerId: string }) => {
    const response = await apiRequest<void>({
      method: 'DELETE',
      url: API_ENDPOINTS.EVENTS.REMOVE_SINGER(eventId, singerId),
    })

    if (!response.success) {
      throw new Error(response.message || 'Failed to remove singer')
    }

    return { singerId }
  },
)

/**
 * @summary Singers slice.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
const singersSlice = createSlice({
  name: 'singers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearSingers: (state) => {
      state.singers = []
      state.currentEventId = null
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // Fetch singers
    builder
      .addCase(fetchSingers.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchSingers.fulfilled, (state, action) => {
        state.isLoading = false
        state.singers = action.payload.singers
        state.currentEventId = action.payload.eventId
        state.error = null
      })
      .addCase(fetchSingers.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch singers'
      })
      // Remove singer
      .addCase(removeSinger.fulfilled, (state, action) => {
        state.singers = state.singers.filter((singer) => singer.id !== action.payload.singerId)
      })
      .addCase(removeSinger.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to remove singer'
      })
  },
})

export const { clearError, clearSingers } = singersSlice.actions
export default singersSlice.reducer

